/* tslint:disable:no-shadowed-variable */
import * as R from 'ramda'
import camelcaseKeysDeep from 'camelcase-keys-deep'
import { ModuleNames } from '@/store/module-names'
import { LivePatientsWithHistories, PatientWithLiveDataLatestState } from './types'
import { ActionNames, MutationNames } from './keys'
import { getters } from './getters'
import {
  GetPatientWithLiveDataLatestDto,
  getPatientWithLiveDataLatestQuery,
  PatientWithLiveDataLatestSchema,
} from '@/jsonapi/PatientWithLiveDataLatest'

import { DataPoint } from '@/jsonapi/CommonTypes'
import { LIVE_DASH_BOARD_MAX_DATA_LENGTH, LIVE_DASH_BOARD_UPDATE_TIMESTAMP } from '@/constants/live-dashboard'

const state: PatientWithLiveDataLatestState = {
  patientWithLiveDataLatests: [],
  lastUpdate: null,
  livePatientsWithHistories: [],
}

const mutations = {
  [MutationNames.updatePatientWithLiveDataLatests](state: PatientWithLiveDataLatestState, payload): void {
    state.patientWithLiveDataLatests = payload
    state.lastUpdate = new Date().getTime()
  },
  [MutationNames.resetPatientWitLiveData](state: PatientWithLiveDataLatestState): void {
    state.livePatientsWithHistories = []
    state.patientWithLiveDataLatests = []
    state.lastUpdate = null
  },
  [MutationNames.updatePatientWithHistories](state: PatientWithLiveDataLatestState, payload): void {
    state.livePatientsWithHistories = payload
  },
}

const actions = {
  async [ActionNames.getPatientWithLiveDataLatests](
    { commit, state },
    payload: GetPatientWithLiveDataLatestDto
  ): Promise<void> {
    const results = camelcaseKeysDeep(await getPatientWithLiveDataLatestQuery(payload))
    const newState = mergeLiveDataAvoidingNull(state.patientWithLiveDataLatests, results)
    const newPatientWithHistories = getNewLiveDataPatientWithHistories(state.livePatientsWithHistories, results)
    commit(MutationNames.updatePatientWithLiveDataLatests, newState)
    commit(MutationNames.updatePatientWithHistories, newPatientWithHistories)
  },
}

export default {
  name: ModuleNames.patientWithLiveDataLatest,
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
}

function mergeLiveDataAvoidingNull(
  stateData: PatientWithLiveDataLatestSchema[],
  apiResults: PatientWithLiveDataLatestSchema[]
) {
  const removeCandidateProps = ['oxygenSaturation', 'pulseRate', 'bodyTemperature', 'bodyWeight']
  const apiResultsRemovedEmptyValues = [...apiResults].map(livedata => {
    removeCandidateProps.forEach(name => {
      if (livedata.attributes[name] === null) {
        delete livedata.attributes[name]
      }
    })
    return livedata
  })

  const stateDataById = R.indexBy(R.prop('id'), [...stateData])
  const apiResultsById = R.indexBy(R.prop('id'), apiResultsRemovedEmptyValues)
  const merged = R.mergeDeepRight(stateDataById, apiResultsById)
  return Object.values(merged)
}

function getNewLiveDataPatientWithHistories(
  stateData: readonly LivePatientsWithHistories[],
  apiResults: readonly PatientWithLiveDataLatestSchema[]
): LivePatientsWithHistories[] {
  return apiResults.map(liveData => {
    const existingData = stateData.find(stateLiveData => stateLiveData.id === liveData.id)
    const { oxygenSaturation, pulseRate, timezone, firstName, lastName } = liveData.attributes
    if (existingData == null) {
      return {
        id: liveData.id,
        oxygenSaturations: oxygenSaturation ? [oxygenSaturation] : [],
        pulseRates: pulseRate ? [pulseRate] : [],
        firstName,
        lastName,
        timezone,
      } as LivePatientsWithHistories
    } else {
      const cloned = R.clone<LivePatientsWithHistories>(existingData)

      if (oxygenSaturation) {
        if (cloned.oxygenSaturations.length) {
          const lastDp = cloned.oxygenSaturations[cloned.oxygenSaturations.length - 1]
          if (oxygenSaturation.timestamp - lastDp.timestamp > LIVE_DASH_BOARD_UPDATE_TIMESTAMP) {
            cloned.oxygenSaturations.push(oxygenSaturation)
          } else {
            lastDp.timestamp = oxygenSaturation.timestamp
            lastDp.value = oxygenSaturation.value
          }
        } else {
          cloned.oxygenSaturations.push(oxygenSaturation)
        }
      }

      if (pulseRate) {
        if (cloned.pulseRates.length) {
          const lastDp = cloned.pulseRates[cloned.pulseRates.length - 1]
          if (pulseRate.timestamp - lastDp.timestamp > LIVE_DASH_BOARD_UPDATE_TIMESTAMP) {
            cloned.pulseRates.push(pulseRate)
          } else {
            lastDp.timestamp = pulseRate.timestamp
            lastDp.value = pulseRate.value
          }
        } else {
          cloned.pulseRates.push(pulseRate)
        }
      }

      if (cloned.oxygenSaturations.length > LIVE_DASH_BOARD_MAX_DATA_LENGTH) cloned.oxygenSaturations.shift()
      if (cloned.pulseRates.length > LIVE_DASH_BOARD_MAX_DATA_LENGTH) cloned.pulseRates.shift()
      return cloned
    }
  })
}
